import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet, CalendarX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { fmtMoney, fmtNumber, fmtDate, fmtMonth } from '../components/Expenses/format';
import PageHeader from '../components/Shared/PageHeader';
import { btn, c, card, tbl } from '../theme';

// load_mode is deliberately not a control on this page. The endpoint accepts
// 'initial_backfill', which turns window reconciliation off — correct for the
// one-time historical extract, which has nothing prior to reconcile against,
// and wrong for everything else. That load has already happened, so exposing
// the choice here would offer one already-spent option whose only remaining
// use is by mistake: a recurring file marked as a backfill lets upstream
// amendments accumulate beside their originals and inflates the totals with
// nothing to warn you. The server defaults to monthly_delta; we send nothing
// and inherit it.

export default function ExpenseUpload() {
  const qc = useQueryClient();
  const [file,     setFile]     = useState(null);
  const [progress, setProgress] = useState(0);
  const [preview,  setPreview]  = useState(null);   // { batch, summary }
  const [ackWipe,  setAckWipe]  = useState(false);
  const inputRef = useRef(null);

  const { data: batchData, isLoading: batchesLoading } = useQuery({
    queryKey: ['expense-batches'],
    queryFn:  () => api.get('/expenses/batches'),
    refetchInterval: 15000,
  });
  const batches = batchData?.batches || [];

  // Which months already hold data. A delta feed's characteristic failure is a
  // silently missed drop, and "did we skip a month" is exactly the question you
  // have while standing on the upload page — so answer it before the upload
  // rather than leaving it to be noticed on the dashboard later.
  const { data: coverage } = useQuery({
    queryKey: ['expense-coverage'],
    queryFn:  () => api.get('/expenses/coverage'),
  });
  const gaps = coverage?.gaps || [];

  // ── Step 1: upload + parse (preview only) ──────────────────
  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/expenses/upload', fd, setProgress);
    },
    onSuccess: (data) => { setPreview(data); setAckWipe(false); setProgress(0); },
    onError:   (err) => { toast.error(err.message || 'Upload failed.'); setProgress(0); },
  });

  // ── Step 2a: commit ────────────────────────────────────────
  const commitMutation = useMutation({
    mutationFn: () => api.commit(`/expenses/batches/${preview.batch.id}/commit`),
    onSuccess: (data) => {
      toast.success(
        `Committed ${fmtNumber(data.inserted)} new line(s)` +
        `${data.skipped ? `, ${fmtNumber(data.skipped)} already present` : ''}` +
        `${data.superseded ? `, ${fmtNumber(data.superseded)} superseded` : ''}.`
      );
      resetAll();
      // Every dashboard query is keyed 'expense-*'; a prefix predicate keeps
      // this correct as panels are added rather than listing them one by one.
      qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('expense-') });
    },
    onError: (err) => toast.error(err.message || 'Commit failed.'),
  });

  // ── Step 2b: cancel ────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/expenses/batches/${preview.batch.id}/cancel`),
    onSuccess: () => {
      toast('Upload discarded — nothing was written.');
      resetAll();
      qc.invalidateQueries({ queryKey: ['expense-batches'] });
    },
    onError: (err) => toast.error(err.message || 'Cancel failed.'),
  });

  function resetAll() {
    setFile(null); setPreview(null); setProgress(0); setAckWipe(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  const p    = preview?.summary;
  const busy = uploadMutation.isPending || commitMutation.isPending || cancelMutation.isPending;
  // A supersede count out of proportion to the window is the signature of a
  // truncated or partial file. Committing one wipes the rest of the window, so
  // the confirm button stays disabled until that is acknowledged explicitly.
  const needsAck   = !!p?.supersede_warning;
  const canCommit  = !busy && (!needsAck || ackWipe);

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Expense Upload"
        subtitle="Import a Workday expense-line extract. The file is parsed and previewed first — nothing is written until you confirm. A file is treated as authoritative for its own date range: lines already stored inside that range that the file no longer contains are superseded, which is what keeps an upstream amendment from double-counting against its original."
      />

      {/* ── Coverage gaps ─────────────────────────────────────── */}
      {gaps.length > 0 && !preview && (
        <div style={s.warnBlock}>
          <div style={s.warnHead}>
            <CalendarX size={14} /> {gaps.length} month(s) with no expense data
          </div>
          <div style={s.chips}>
            {gaps.map((g) => <span key={g} style={s.warnChip}>{fmtMonth(g)}</span>)}
          </div>
          <div style={s.warnFoot}>{coverage?.note}</div>
        </div>
      )}

      {/* ── Step 1: drop zone (hidden once we have a preview) ── */}
      {!preview && (
        <div style={s.card}>
          <div
            style={{ ...s.dropZone, borderColor: file ? c.success : c.borderStrong }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            <UploadCloud size={26} color={file ? c.success : c.textFaint} />
            <div style={s.dropText}>
              {file ? (
                <><strong style={{ color: c.success }}>{file.name}</strong><br />
                  <span style={{ color: c.textFaint }}>{(file.size / 1024).toFixed(0)} KB</span></>
              ) : (
                <><strong>Click to select</strong> or drag &amp; drop<br />
                  <span style={{ color: c.textFaint }}>Workday expense extract .xlsx or .xls</span></>
              )}
            </div>
          </div>

          {uploadMutation.isPending && (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
              <span style={s.progressLabel}>{progress}%</span>
            </div>
          )}

          <button
            style={{ ...s.primaryBtn, opacity: file && !busy ? 1 : 0.5 }}
            disabled={!file || busy}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? 'Parsing…' : 'Parse & Preview'}
          </button>
        </div>
      )}

      {/* ── Step 2: preview + commit/cancel ───────────────────── */}
      {p && (
        <div style={s.card}>
          <div style={s.previewHead}>
            <FileSpreadsheet size={18} color={c.navy} />
            <span style={s.previewTitle}>Preview — {preview.batch.file_name || file?.name || 'expense file'}</span>
          </div>

          <div style={s.statGrid}>
            <Stat label="Rows in File"  value={fmtNumber(p.row_count)} />
            <Stat label="New Lines"     value={fmtNumber(p.to_insert)} />
            <Stat label="Already Present" value={fmtNumber(p.redelivered)}
                  hint="Re-delivered rows. They insert nothing." />
            <Stat label="Total Amount"  value={fmtMoney(p.total_amount)} />
            <Stat label="Card"          value={fmtMoney(p.card_amount)} />
            <Stat label="Reimbursement" value={fmtMoney(p.reimb_amount)} />
          </div>

          <div style={s.rangeRow}>
            <span style={s.rangeLabel}>Dates in file</span>
            <span style={s.rangeVal}>{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</span>
            {p.window_start && (
              <>
                <span style={s.rangeLabel}>Reconciled window</span>
                <span style={s.rangeVal}>{fmtDate(p.window_start)} → {fmtDate(p.window_end)}</span>
              </>
            )}
          </div>

          {/* Repeats inside this one file — kept, not merged, so say so. */}
          {p.repeat_txn_count > 0 && (
            <div style={s.noteBlock}>
              {fmtNumber(p.repeat_txn_count)} line(s) are byte-identical repeats of another line in
              this same file. They are kept as separate transactions, not merged — identical
              amounts on one report are usually genuine separate authorisations.
            </div>
          )}

          {/* Supersede — the one thing on this page that removes data. */}
          {p.to_supersede_count > 0 && (
            <div style={needsAck ? s.dangerBlock : s.warnBlock}>
              <div style={needsAck ? s.dangerHead : s.warnHead}>
                <AlertTriangle size={14} />
                {fmtNumber(p.to_supersede_count)} stored line(s) worth {fmtMoney(p.to_supersede_amount)} will
                be superseded — {(p.supersede_ratio * 100).toFixed(1)}% of the window
              </div>
              <div style={needsAck ? s.dangerBody : s.warnFoot}>
                These sit inside this file&apos;s date range but are not in the file. Committing stamps
                them superseded — a soft mark, never a delete, so it stays reversible.
                {needsAck && ' A share this high is the signature of a truncated or partial export. Check the file covers the whole window before continuing.'}
              </div>
              {p.to_supersede_sample?.length > 0 && (
                <div style={s.sampleWrap}>
                  <div style={{ overflowX: 'auto' }}>
                    {/* Wide table: it scrolls inside this, so the page does not. */}
                  <table style={s.sampleTable}>
                    <thead>
                      <tr>{['Report', 'Date', 'Item', 'Employee', 'Amount', 'Memo'].map((h) => (
                        <th key={h} style={s.sTh}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {p.to_supersede_sample.map((r, i) => (
                        <tr key={i} style={s.sTr}>
                          <td style={s.sTd}>{r.expense_report || '—'}</td>
                          <td style={s.sTd}>{fmtDate(r.line_date)}</td>
                          <td style={s.sTd}>{r.expense_item || '—'}</td>
                          <td style={s.sTd}>{r.employee_name || '—'}</td>
                          <td style={s.sTdR}>{fmtMoney(r.line_amount)}</td>
                          <td style={s.sTd} title={r.memo}>{(r.memo || '').slice(0, 40) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
              {p.to_supersede_count > (p.to_supersede_sample?.length || 0) && (
                <div style={s.warnFoot}>
                  Showing the first {fmtNumber(p.to_supersede_sample?.length || 0)} of {fmtNumber(p.to_supersede_count)}.
                </div>
              )}
            </div>
          )}

          {/* Unmapped reference data — commits fine, but shows as <Unmapped>. */}
          {p.unmapped_items?.length > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}>
                <AlertTriangle size={14} /> {fmtNumber(p.unmapped_items.length)} expense item(s) with no spend category
              </div>
              <div style={s.chips}>
                {p.unmapped_items.slice(0, 15).map((u) => (
                  <span key={u.item} style={s.warnChip}>{u.item} · {fmtNumber(u.count)}</span>
                ))}
                {p.unmapped_items.length > 15 && (
                  <span style={s.warnChip}>…and {p.unmapped_items.length - 15} more</span>
                )}
              </div>
              <div style={s.warnFoot}>
                These still commit — they group under &lt;Unmapped&gt; until you add them in Expense Mappings.
              </div>
            </div>
          )}

          {p.unknown_cost_centers?.length > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}>
                <AlertTriangle size={14} /> {fmtNumber(p.unknown_cost_centers.length)} unrecognised cost center(s)
              </div>
              <div style={s.chips}>
                {p.unknown_cost_centers.slice(0, 15).map((u) => (
                  <span key={u.name} style={s.warnChip}>{u.name} · {fmtNumber(u.count)}</span>
                ))}
                {p.unknown_cost_centers.length > 15 && (
                  <span style={s.warnChip}>…and {p.unknown_cost_centers.length - 15} more</span>
                )}
              </div>
            </div>
          )}

          {p.warnings?.length > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> {fmtNumber(p.warnings.length)} row warning(s)</div>
              <ul style={s.warnList}>
                {p.warnings.slice(0, 8).map((w, i) => <li key={i} style={s.warnItem}>{w}</li>)}
                {p.warnings.length > 8 && <li style={s.warnItem}>…and {p.warnings.length - 8} more.</li>}
              </ul>
            </div>
          )}

          {/* Months in the file — makes a wrong-file upload obvious. */}
          {p.months?.length > 0 && (
            <div style={s.subBlock}>
              <div style={s.subLabel}>Months in File</div>
              <div style={s.chips}>
                {p.months.map((m) => (
                  <span key={m.month} style={s.chip}>
                    {fmtMonth(m.month)} · {fmtNumber(m.rows)} rows · {fmtMoney(m.amount)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {needsAck && (
            <label style={s.ackRow}>
              <input type="checkbox" checked={ackWipe} onChange={(e) => setAckWipe(e.target.checked)} />
              <span>
                I have checked this file covers its whole date range, and I accept that{' '}
                {fmtNumber(p.to_supersede_count)} stored line(s) will be superseded.
              </span>
            </label>
          )}

          <div style={s.actionRow}>
            <button style={{ ...s.primaryBtn, opacity: canCommit ? 1 : 0.5 }} disabled={!canCommit}
                    onClick={() => commitMutation.mutate()}>
              <CheckCircle size={15} /> {commitMutation.isPending ? 'Committing…' : 'Confirm & Commit'}
            </button>
            <button style={{ ...s.secondaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy}
                    onClick={() => cancelMutation.mutate()}>
              <XCircle size={15} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Ingestion history ─────────────────────────────────── */}
      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <div style={{ overflowX: 'auto' }}>
            {/* Wide table: it scrolls inside this, so the page does not. */}
          <table style={s.logTable}>
            <thead>
              <tr>{['File', 'Mode', 'Status', 'Period', 'Rows', 'Inserted', 'Amount', 'By', 'When'].map((h) => (
                <th key={h} style={s.logTh}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const st = STATUS_STYLE[b.status] || STATUS_STYLE.preview;
                return (
                  <tr key={b.id} style={s.logTr}>
                    <td style={s.logTd} title={b.file_name}>{b.file_name?.slice(-40)}</td>
                    <td style={s.logTd}>{b.load_mode === 'initial_backfill' ? 'Backfill' : 'Delta'}</td>
                    <td style={s.logTd}>
                      <span style={{ ...s.statusPill, color: st.color, background: st.bg }} title={b.error_message || undefined}>
                        {st.icon} {b.status}
                      </span>
                    </td>
                    <td style={s.logTd}>{b.period_start ? `${fmtDate(b.period_start)} → ${fmtDate(b.period_end)}` : '—'}</td>
                    <td style={s.logTdR}>{fmtNumber(b.row_count)}</td>
                    <td style={s.logTdR}>{b.status === 'committed' ? fmtNumber(b.inserted_count) : '—'}</td>
                    <td style={s.logTdR}>{b.total_amount === null ? '—' : fmtMoney(b.total_amount)}</td>
                    <td style={s.logTd}>{b.uploaded_by_name || (b.source === 'ftp' ? 'System' : '—')}</td>
                    <td style={s.logTd}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
              {!batches.length && <tr><td colSpan={9} style={s.emptyLog}>No imports yet.</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div style={s.stat} title={hint}>
      <div style={s.statVal}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: c.success,   bg: c.successTint, icon: <CheckCircle size={12} /> },
  preview:   { color: c.info,      bg: c.infoTint,    icon: <AlertTriangle size={12} /> },
  cancelled: { color: c.textFaint, bg: c.canvas,      icon: <XCircle size={12} /> },
  failed:    { color: c.danger,    bg: c.dangerTint,  icon: <XCircle size={12} /> },
};

const s = {
  page:        { maxWidth: '1000px', margin: '0 auto' },
  card:        { ...card.base, padding: '24px', marginBottom: '24px' },
  dropZone:    { border: '2px dashed', borderRadius: '10px', padding: '32px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', transition: 'border-color 0.2s' },
  dropText:    { fontSize: '13px', color: c.textStrong, lineHeight: 1.6 },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: c.border, borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: c.navy, borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '11px', fontWeight: 600, color: c.textStrong, minWidth: '30px' },
  primaryBtn:  { ...btn.primary },
  secondaryBtn:{ ...btn.secondary },
  previewHead: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' },
  previewTitle:{ fontSize: '14px', fontWeight: 700, color: c.text },
  statGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '16px' },
  stat:        { ...card.sunk, padding: '12px 14px' },
  statVal:     { fontSize: '18px', fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums' },
  statLabel:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textFaint, marginTop: '3px' },
  rangeRow:    { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', marginBottom: '18px' },
  rangeLabel:  { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textFaint },
  rangeVal:    { fontSize: '12px', color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  subBlock:    { marginBottom: '18px' },
  subLabel:    { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginBottom: '8px' },
  chips:       { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip:        { fontSize: '12px', color: c.textStrong, background: '#EEF2F7', borderRadius: '6px', padding: '4px 10px' },
  warnChip:    { fontSize: '12px', color: c.warningText, background: c.warningTint, borderRadius: '6px', padding: '4px 10px' },
  noteBlock:   { ...card.sunk, padding: '10px 14px', fontSize: '12px', color: c.textMuted, lineHeight: 1.55, marginBottom: '18px' },
  warnBlock:   { background: '#FFFBEB', border: `1px solid ${c.warningTint}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' },
  warnHead:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: c.warning, marginBottom: '6px' },
  warnList:    { margin: 0, paddingLeft: '18px' },
  warnItem:    { fontSize: '12px', color: c.warningText, lineHeight: 1.6 },
  warnFoot:    { fontSize: '11px', color: c.warningText, marginTop: '6px', lineHeight: 1.5 },
  // Deliberately louder than warnBlock: this is the state where committing
  // removes data that is probably still good.
  dangerBlock: { background: c.dangerTint, border: `1px solid ${c.danger}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' },
  dangerHead:  { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: c.dangerText, marginBottom: '6px' },
  dangerBody:  { fontSize: '11px', color: c.dangerText, marginBottom: '8px', lineHeight: 1.5 },
  sampleWrap:  { border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'auto', background: c.surface, marginTop: '8px' },
  sampleTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  sTh:         { ...tbl.thSm, whiteSpace: 'nowrap' },
  sTr:         { borderBottom: `1px solid ${c.canvas}` },
  sTd:         { ...tbl.tdSm, whiteSpace: 'nowrap' },
  sTdR:        { ...tbl.tdRSm },
  ackRow:      { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: c.dangerText, lineHeight: 1.5, marginBottom: '14px', cursor: 'pointer' },
  actionRow:   { display: 'flex', gap: '10px', marginTop: '6px' },
  logSection:  { ...card.base, overflow: 'hidden' },
  logTitle:    { ...card.head },
  logTable:    { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  logTh:       { ...tbl.th },
  logTr:       { borderBottom: `1px solid ${c.canvas}` },
  logTd:       { ...tbl.td },
  logTdR:      { ...tbl.tdR },
  statusPill:  { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: c.textFaint },
};
